import React, { useEffect, useState, useRef } from "react";
import QRCode from "react-qr-code";
import HttpClient from "../../lib/HttpClient";
import ReceptionistProfile from "../../models/domain/ReceptionistProfile";
import utils from "../../Utils";
import { Dropdown, Form, InputGroup } from "react-bootstrap";
import { PDFDownloadLink, BlobProvider } from "@react-pdf/renderer";
import InvoicePdf from "../prints/InvoicePdf";

interface InvoiceProps {
  invoice: any;
  onCancel: () => void;
  onPrint: (data: any) => Promise<boolean>;
  invoiceDone: boolean | undefined | null;
  selectedInvoiceIndex: number;
  setSelectedInvoiceIndex: (index: number) => void;
}

interface PaymentDetails {
  modeOfPayment: string;
  bankName: string;
  cardType: string;
  transactionNo: string;
}

function getFormattedDate(separator: string, dateString?: string): string {
  let date: Date;

  if (dateString) {
    const [dd, mm, yyyy] = dateString.split("/");
    date = new Date(`${yyyy}-${mm}-${dd}`); // ISO-compatible format
  } else {
    date = new Date();
  }

  const isValidDate = !isNaN(date.getTime());
  const finalDate = isValidDate ? date : new Date();

  const day = String(finalDate.getDate()).padStart(2, "0");
  const month = String(finalDate.getMonth() + 1).padStart(2, "0");
  const year = finalDate.getFullYear();

  return `${day}${separator}${month}${separator}${year}`;
}

function getFormattedCurrentTime(): string {
  const now = new Date();
  const hh = String(now.getHours()).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  const ss = String(now.getSeconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

const MedicalInvoice: React.FC<InvoiceProps> = (props) => {
  const {
    onCancel,
    onPrint,
    invoice,
    invoiceDone,
    selectedInvoiceIndex,
    setSelectedInvoiceIndex,
  } = props;
  const website = "ic4.health";

  const printDateTime = `${getFormattedDate(
    "-"
  )} | ${getFormattedCurrentTime()}`;
  const invoiceDate = getFormattedDate(
    "-",
    invoice?.[selectedInvoiceIndex].date
  );
  const receiptDate = getFormattedDate(
    "/",
    invoice?.[selectedInvoiceIndex].date
  );

  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const [userProfile, setUserProfile] = useState<ReceptionistProfile | null>(
    utils.retrieveUserProfile()
  );

  const [paymentDetails, setPaymentDetails] = useState<PaymentDetails>({
    modeOfPayment: invoice[selectedInvoiceIndex].paymentMode,
    bankName: invoice[selectedInvoiceIndex].bankName,
    cardType: invoice[selectedInvoiceIndex].cardChequeType,
    transactionNo: invoice[selectedInvoiceIndex].transactionId,
  });

  useEffect(() => {
    if (invoice[selectedInvoiceIndex]) {
      setPaymentDetails({
        modeOfPayment: invoice[selectedInvoiceIndex].paymentMode,
        bankName: invoice[selectedInvoiceIndex].bankName,
        cardType: invoice[selectedInvoiceIndex].cardChequeType,
        transactionNo: invoice[selectedInvoiceIndex].transactionId,
      });
    }
  }, [selectedInvoiceIndex, invoice]);

  const [services, setServices] = useState([
    {
      srNo: 1,
      description: invoice?.[selectedInvoiceIndex].consultDescription,
      qty: 1,
      amount: invoice?.[selectedInvoiceIndex].consultInvAmount,
    },
  ]);

  useEffect(() => {
    if (invoice?.[selectedInvoiceIndex]) {
      setServices([
        {
          srNo: 1,
          description: invoice[selectedInvoiceIndex].consultDescription,
          qty: 1,
          amount: invoice[selectedInvoiceIndex].consultInvAmount,
        },
      ]);
    }
  }, [selectedInvoiceIndex, invoice]);

  const isEditable = selectedInvoiceIndex === 0 && invoiceDone === false;

  const handleSelectPaymentMode = (mode: string | null) => {
    setPaymentDetails((prevDetails) => ({
      ...prevDetails,
      modeOfPayment: mode || "CASH",
    }));
  };

  function printPDF(pdfUrl: string | URL | undefined) {
    function isActualDesktop(): boolean {
      const ua = navigator.userAgent.toLowerCase();

      // Check for desktop operating systems in user agent
      const isWindows = /windows nt/.test(ua);
      const isMac = /macintosh/.test(ua);
      const isLinux = /linux(?! android)/.test(ua) && !/android/.test(ua);
      const isDesktopOS = isWindows || isMac || isLinux;

      // Check if user agent does NOT indicate mobile devices
      const notMobileUA = !/iphone|ipad|ipod|android|mobile/.test(ua);

      // Early exit if not a desktop OS or UA indicates mobile
      if (!isDesktopOS || !notMobileUA) {
        return false;
      }

      // Use navigator.userAgentData if available for more accurate mobile detection
      if (
        typeof (navigator as any).userAgentData !== "undefined" &&
        (navigator as any).userAgentData.mobile
      ) {
        return false;
      }

      // Check pointer and hover capabilities
      const primaryPointerFine = window.matchMedia("(pointer: fine)").matches;
      const anyHover = window.matchMedia("(any-hover: hover)").matches;

      // Touch-enabled desktops should still return true
      return primaryPointerFine && anyHover;
    }

    // Set the src attribute of the iframe
    if (!isActualDesktop()) {
      const w = window.open(pdfUrl, "_blank");
      if (w) {
        try {
          w.print();
        } catch (err) {
          console.error("Print failed", err);
        }
      }
    } else {
      if (iframeRef.current && pdfUrl) {
        iframeRef.current.src = pdfUrl as string;
        iframeRef.current.onload = function () {
          iframeRef.current?.contentWindow?.print();
        };
      } else {
        console.warn("iframeRef is not available");
      }
    }
  }

  const qrCodeData = `https://ic4.health/patient/login?cid=${userProfile?.clinicId}`;

  const fullAddress = [
    invoice?.[selectedInvoiceIndex].clinicAddress?.streetAddress,
    invoice?.[selectedInvoiceIndex].clinicAddress?.locality,
    invoice?.[selectedInvoiceIndex].clinicAddress?.city,
    invoice?.[selectedInvoiceIndex].clinicAddress?.state,
    invoice?.[selectedInvoiceIndex].clinicAddress?.zipCode,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div
      className="container bg-white p-4 position-relative"
      style={{ maxWidth: "800px", boxShadow: "0 0 10px rgba(0,0,0,0.1)" }}
    >
      {/* Header */}
      <div className="row mb-3">
        <div className="col">
          <h3 className="fw-bold">
            {invoice?.[selectedInvoiceIndex].clinicName}
          </h3>
          <p className="mb-1">
            {invoice?.[selectedInvoiceIndex].clinicAddress?.address}
          </p>
          <p className="mb-1">{fullAddress}</p>
          <p className="mb-1">
            Phone: {invoice?.[selectedInvoiceIndex].clinicPhone} | Email:{" "}
            {invoice?.[selectedInvoiceIndex].clinicEmail}
          </p>
          <p className="mb-1">
            Website: {website}
            {invoice?.[selectedInvoiceIndex].gstNumber && (
              <> | GST Number: {invoice[selectedInvoiceIndex].gstNumber}</>
            )}
          </p>
        </div>
        <div className="col-3 text-end">
          <QRCode value={qrCodeData} size={100} />
          <div className="mt-2 d-flex align-items-center justify-content-end gap-2">
            <span>Date:</span>
            <Dropdown>
              <Dropdown.Toggle
                variant="secondary"
                id="dropdown-basic"
                size="sm"
              >
                {invoice?.[selectedInvoiceIndex].date}
              </Dropdown.Toggle>

              <Dropdown.Menu>
                {invoice.map((item: { date: string }, index: number) => (
                  <Dropdown.Item
                    key={index}
                    onClick={() => setSelectedInvoiceIndex(index)}
                  >
                    {item.date}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </div>

      <hr className="opacity-25" />

      {/* Patient and Doctor Info */}
      <div className="row mb-3">
        <div className="col-6">
          <div className="row mb-2">
            <div className="col-5">Patient Name:</div>
            <div className="col-7 fw-bold text-end">
              {invoice?.[selectedInvoiceIndex].patientName}
            </div>
          </div>
          <div className="row mb-2">
            <div className="col-5">Registration No:</div>
            <div className="col-7 fw-bold text-end">
              {invoice?.[selectedInvoiceIndex].patientId}
            </div>
          </div>
          <div className="row mb-2">
            <div className="col-5">Contact Number:</div>
            <div className="col-7 fw-bold text-end">
              {invoice?.[selectedInvoiceIndex].patientMobile}
            </div>
          </div>
        </div>
        <div className="col-6 border-start">
          <div className="row mb-2">
            <div className="col-5">Primary Doctor:</div>
            <div className="col-7 fw-bold text-end">
              {invoice?.[selectedInvoiceIndex].doctorName}
            </div>
          </div>
          <div className="row mb-2">
            <div className="col-5">Doctor Reg. No:</div>
            <div className="col-7 fw-bold text-end">
              {invoice?.[selectedInvoiceIndex].doctorRegNo}
            </div>
          </div>
          <div className="row mb-2">
            <div className="col-5">Department:</div>
            <div className="col-7 fw-bold text-end">
              {invoice?.[selectedInvoiceIndex].doctorSpeciality}
            </div>
          </div>
        </div>
      </div>

      {/* Service Table */}
      <div className="table-responsive mb-3">
        <table className="table border border-gray-300">
          <thead className="table-light border-bottom border-gray-300">
            <tr className="fw-bold text-start">
              <th
                className="p-2 border-end border-gray-300"
                style={{ width: "10%" }}
              >
                Sr. No
              </th>
              <th
                className="p-2 border-end border-gray-300"
                style={{ width: "60%" }}
              >
                Service Description
              </th>
              <th
                className="p-2 text-center border-end border-gray-300"
                style={{ width: "10%" }}
              >
                Qty
              </th>
              <th className="p-2 text-end" style={{ width: "20%" }}>
                Amount
              </th>
            </tr>
          </thead>
          <tbody className="border-top border-gray-300">
            {services?.map((service, index) => (
              <tr key={service.srNo} className="border-bottom border-gray-300">
                <td className="p-2 fw-bold border-end border-gray-300">
                  {service.srNo}
                </td>
                <td className="p-2 border-end border-gray-300">
                  {!isEditable ? (
                    <span> {service?.description}</span>
                  ) : (
                    <Form.Control
                      type="text"
                      // className="text-end"
                      value={service.description}
                      onChange={(e) => {
                        const updatedDescription = e.target.value;
                        setServices((prevServices) =>
                          prevServices.map((existingService, serviceIndex) =>
                            serviceIndex === index
                              ? {
                                  ...existingService,
                                  description: updatedDescription,
                                }
                              : existingService
                          )
                        );
                      }}
                    />
                  )}
                </td>
                <td className="p-2 text-center border-end border-gray-300">
                  {service.qty}
                </td>
                <td className="p-2 text-end">
                  {!isEditable ? (
                    <span> ₹ {service?.amount}</span>
                  ) : (
                    <InputGroup>
                      <InputGroup.Text>₹</InputGroup.Text>
                      <Form.Control
                        type="number"
                        value={service.amount}
                        onChange={(e) => {
                          const updatedAmount = e.target.value;
                          setServices((prevServices) =>
                            prevServices.map((existingService, serviceIndex) =>
                              serviceIndex === index
                                ? { ...existingService, amount: updatedAmount }
                                : existingService
                            )
                          );
                        }}
                      />
                    </InputGroup>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div className="row mb-3">
        <div className="col-6"></div>
        <div className="col-6">
          <div className="row mb-2">
            <div className="col-6 text-end">Subtotal:</div>
            <div className="col-6 text-end">₹ {services[0]?.amount}</div>
          </div>
          <div className="row mb-2 border-top border-bottom border-light">
            <div className="col-6 text-end fw-bold">Net Payable Amount:</div>
            <div className="col-6 text-end">₹ {services[0]?.amount}</div>
          </div>
        </div>
      </div>
      <hr />

      {/* Payment Details */}
      <div className="mb-4">
        <h5 className="fw-bold mb-2">Payment Details</h5>
        <div className="row mb-2">
          <div className="col-6">
            <div className="row mb-2">
              <div className="col-6">Mode of Payment:</div>
              <div className="col-6">
                {!isEditable ? (
                  <span>{paymentDetails.modeOfPayment}</span>
                ) : (
                  <Dropdown onSelect={(val) => handleSelectPaymentMode(val)}>
                    <Dropdown.Toggle
                      variant="outline-secondary"
                      style={{ minWidth: "100px", color: "black" }}
                    >
                      {paymentDetails.modeOfPayment}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item eventKey="CASH">Cash</Dropdown.Item>
                      <Dropdown.Item eventKey="CARD">Card</Dropdown.Item>
                      <Dropdown.Item eventKey="UPI">UPI</Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                )}
              </div>
            </div>

            <div className="row mb-2">
              <div className="col-6">Receipt Number:</div>
              <div className="col-6 fw-bold">
                {invoice?.[selectedInvoiceIndex].receiptNo}
              </div>
            </div>
            <div className="row mb-2">
              <div className="col-6">Receipt Date:</div>
              <div className="col-6 fw-bold">{receiptDate}</div>
            </div>
          </div>

          <div
            className={`col-6 ${
              paymentDetails.modeOfPayment === "CASH" ? "d-none" : ""
            }`}
          >
            {/* Bank Name */}
            <div className="row mb-2 align-items-center">
              <div className="col-6">Bank Name:</div>
              <div className="col-6">
                {!isEditable ? (
                  <span>{paymentDetails.bankName}</span>
                ) : (
                  <Form.Control
                    type="text"
                    value={paymentDetails.bankName}
                    onChange={(e) =>
                      setPaymentDetails((prev) => ({
                        ...prev,
                        bankName: e.target.value,
                      }))
                    }
                  />
                )}
              </div>
            </div>

            {/* Card Type */}
            {paymentDetails.modeOfPayment === "CARD" && (
              <div className="row mb-2 align-items-center">
                <div className="col-6">Card Type:</div>
                <div className="col-6">
                  {!isEditable ? (
                    <span>{paymentDetails.cardType}</span>
                  ) : (
                    <Form.Control
                      type="text"
                      value={paymentDetails.cardType}
                      onChange={(e) =>
                        setPaymentDetails((prev) => ({
                          ...prev,
                          cardType: e.target.value,
                        }))
                      }
                    />
                  )}
                </div>
              </div>
            )}

            {/* Transaction Number */}
            <div className="row mb-2 align-items-center">
              <div className="col-6">Transaction Number:</div>
              <div className="col-6">
                {!isEditable ? (
                  <span>{paymentDetails.transactionNo}</span>
                ) : (
                  <Form.Control
                    type="text"
                    value={paymentDetails.transactionNo}
                    onChange={(e) =>
                      setPaymentDetails((prev) => ({
                        ...prev,
                        transactionNo: e.target.value,
                      }))
                    }
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <hr />

      {/* Footer */}
      <div className="row mb-4">
        <div className="col-6">
          <small>Bill Printed By: {userProfile?.name} using IC4.health</small>
        </div>
        <div className="col-6 text-end">
          <small>Date/Time of Printing: {printDateTime}</small>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="d-flex justify-content-end">
        <button className="btn btn_outline me-2" onClick={onCancel}>
          Cancel
        </button>

        {/* <PDFDownloadLink
          document={
            <InvoicePdf
              invoice={invoice}
              services={services}
              paymentDetails={paymentDetails}
              printDateTime={printDateTime}
              invoiceDate={invoiceDate}
              printedBy={userProfile?.name || ""}
              qrCodeData={qrCodeData}
            />
          }
          fileName={`Invoice_${receiptNumber || "download"}.pdf`}
        >
          {({ blob, url, loading, error }) => (
            <button className="btn btn_outline">
              {loading ? "Loading..." : "Download"}
            </button>
          )}
        </PDFDownloadLink> */}

        <BlobProvider
          document={
            <InvoicePdf
              invoice={invoice?.[selectedInvoiceIndex]}
              services={services}
              paymentDetails={paymentDetails}
              printDateTime={printDateTime}
              invoiceDate={invoiceDate}
              printedBy={userProfile?.name || ""}
              qrCodeData={qrCodeData}
              website={website}
            />
          }
        >
          {({ blob, url, loading, error }) => {
            if (loading) {
              return <button className="btn btn_primary">Loading...</button>;
            }

            return (
              <button
                className="btn btn_primary"
                onClick={async () => {
                  if (!blob) return;

                  if (!isEditable) {
                    // Skip API call, just print
                    printPDF(URL.createObjectURL(blob));
                    return;
                  }

                  const success = await onPrint({
                    ...paymentDetails,
                    consultInvAmount: services[0].amount,
                    consultInvCGstAmount: 0,
                    consultInvSGstAmount: 0,
                    consultDescription: services[0].description,
                  });

                  if (success) {
                    printPDF(URL.createObjectURL(blob));
                  }
                }}
              >
                Print
              </button>
            );
          }}
        </BlobProvider>
      </div>
      <iframe ref={iframeRef} style={{ display: "none" }}></iframe>
    </div>
  );
};

// Example usage
export default function MedicalInvoiceContainer({
  appointmentId,
  closeFn,
  invoiceDone,
}: {
  appointmentId: string;
  closeFn: () => void;
  invoiceDone: boolean | undefined | null;
}) {
  const requestGetInvoiceData = (appointmentId: string) =>
    HttpClient.performGet<any>(
      `/api/v1/users/invoice?appointmentId=${appointmentId}`
    );

  const requestUpdateInvoiceData = (appointmentId: string, data: object) =>
    HttpClient.performPost<any>(`/api/v1/users/invoice`, data);

  const [invoiceData, setInvoiceData] = useState<any | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedInvoiceIndex, setSelectedInvoiceIndex] = useState<number>(0);

  const handlePrint = async (data: any): Promise<boolean> => {
    const processedInvoiceData = {
      appointmentId: appointmentId,
      paymentMode: data?.modeOfPayment,
      bankName: data?.bankName,
      bankBranch: "",
      transactionId: data?.transactionNo,
      chequeNo: "",
      cardChequeType: data?.cardType,
      accountHolder: "",
      consultInvAmount: data?.consultInvAmount,
      consultInvCGstAmount: data?.consultInvCGstAmount,
      consultInvSGstAmount: data?.consultInvSGstAmount,
      consultDescription: data?.consultDescription,
    };

    try {
      const res = await requestUpdateInvoiceData(
        appointmentId,
        processedInvoiceData
      );
      console.log("Invoice updated successfully:", res);

      return true;
    } catch (err) {
      console.error("Failed to update invoice:", err);
      return false;
    }
  };

  useEffect(() => {
    const fetchInvoice = async () => {
      setLoading(true);
      try {
        if (!invoiceDone) {
          const updateResponse = await requestUpdateInvoiceData(appointmentId, {
            appointmentId: appointmentId,
          });
          if (!updateResponse?.body?.status?.success) {
            throw new Error("Failed to update invoice");
          }
        }

        const getResponse = await requestGetInvoiceData(appointmentId);
        if (getResponse?.body?.status?.success) {
          setInvoiceData([
            getResponse.body?.invoice ?? {},
            ...(getResponse.body?.history ?? []),
          ]);
        }
      } catch (err) {
        console.error("Failed to fetch invoice:", err);
      } finally {
        setLoading(false);
      }
    };

    if (appointmentId) {
      fetchInvoice();
    }
  }, [appointmentId, invoiceDone]);

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "80vh" }}
      >
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!invoiceData) {
    return (
      <div className="text-danger text-center ">No invoice data found.</div>
    );
  }

  return (
    <MedicalInvoice
      invoice={invoiceData}
      onPrint={handlePrint}
      onCancel={closeFn}
      invoiceDone={invoiceDone}
      selectedInvoiceIndex={selectedInvoiceIndex}
      setSelectedInvoiceIndex={setSelectedInvoiceIndex}
    />
  );
}
